/* Reine Logik für Sprint, Backlog und Archiv (Umbau 12.10.2026, ADR-011). Ohne React, mit Vitest getestet. */
import { kurz, tageBis } from "../../kern/zeit";
import { STATUS, type Aufgabe, type Prioritaet, type Status } from "./logik";

export interface Sprint {
  id: string;
  name: string;
  beginn: string;
  ende: string;
  ziel: string;
  status: "laufend" | "abgeschlossen";
  abgeschlossen_am: string | null;
}

/** Tag plus n Tage (YYYY-MM-DD, ohne Zeitzone) */
export function plusTage(tag: string, n: number): string {
  const d = new Date(`${tag}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** „Sprint 26.10.“: Ein Sprint heißt nach seinem letzten Tag. */
export function sprintName(ende: string): string {
  return `Sprint ${kurz(ende)}`;
}

/** „13.10. bis 26.10.2026“ */
export function zeitraum(s: Pick<Sprint, "beginn" | "ende">): string {
  return `${kurz(s.beginn)} bis ${kurz(s.ende)}${s.ende.slice(0, 4)}`;
}

/** „noch 14 Tage“, „noch 1 Tag“, „endet heute“, „startet am 13.10.“, „Ende überschritten“ */
export function restText(s: Pick<Sprint, "beginn" | "ende">, heuteTag: string): string {
  if (tageBis(s.beginn, heuteTag) > 0) return `startet am ${kurz(s.beginn)}`;
  const n = tageBis(s.ende, heuteTag);
  if (n > 1) return `noch ${n} Tage`;
  if (n === 1) return "noch 1 Tag";
  if (n === 0) return "endet heute";
  return "Ende überschritten";
}

/** Läuft er schon, oder beginnt er erst? */
export function phase(s: Pick<Sprint, "beginn">, heuteTag: string): "Läuft" | "Geplant" {
  return tageBis(s.beginn, heuteTag) > 0 ? "Geplant" : "Läuft";
}

const PRIO_RANG: Record<Prioritaet, number> = { hoch: 0, normal: 1, niedrig: 2 };

/** Liste im Sprint und im Backlog: offen vor erledigt, dann Priorität, dann Titel. */
export function sortierenImSprint<T extends Pick<Aufgabe, "status" | "prioritaet" | "titel">>(liste: readonly T[]): T[] {
  return [...liste].sort(
    (a, b) =>
      Number(a.status === "erledigt") - Number(b.status === "erledigt") ||
      PRIO_RANG[a.prioritaet] - PRIO_RANG[b.prioritaet] ||
      a.titel.localeCompare(b.titel, "de"),
  );
}

/** „Was im Sprint ist, hat eine Person“ (KONZEPT.md): keine Sperre, aber ein Hinweis. Zählt offene Aufgaben ohne Person. */
export function ohnePerson(liste: readonly Pick<Aufgabe, "status" | "personen">[]): number {
  return liste.filter((a) => a.status !== "erledigt" && a.personen.length === 0).length;
}

/** Beim Abschließen stehen Aufgaben ohne Person oben, sonst bleibt die Reihenfolge. */
export function ohnePersonZuerst<T extends Pick<Aufgabe, "personen">>(liste: readonly T[]): T[] {
  return [...liste.filter((a) => a.personen.length === 0), ...liste.filter((a) => a.personen.length > 0)];
}

export type Gruppierung = "keine" | "person" | "ressort" | "status";

export const GRUPPIERUNG: Record<Gruppierung, string> = { keine: "nicht", person: "nach Person", ressort: "nach Ressort", status: "nach Status" };

export interface Gruppe<T> {
  schluessel: string;
  titel: string;
  liste: T[];
}

interface Namen {
  /** Personen in der Reihenfolge, in der sie erscheinen sollen */
  personen: readonly { id: string; name: string }[];
  ressorts: readonly { id: string; name: string }[];
}

/** Gruppen für die Liste im Sprint. Ohne Person und ohne Ressort stehen zuletzt. Leere Gruppen entfallen. */
export function gruppieren<T extends Pick<Aufgabe, "status" | "prioritaet" | "titel" | "person" | "ressort_id"> & { personen?: readonly string[] }>(
  liste: readonly T[],
  art: Gruppierung,
  namen: Namen,
): Gruppe<T>[] {
  const sortiert = sortierenImSprint(liste);
  if (art === "keine") return sortiert.length ? [{ schluessel: "alle", titel: "Alle", liste: sortiert }] : [];
  const reihen: { schluessel: string; titel: string }[] =
    art === "person"
      ? [...namen.personen.map((p) => ({ schluessel: p.id, titel: p.name })), { schluessel: "", titel: "Offen (ohne Person)" }]
      : art === "ressort"
        ? [...namen.ressorts.map((r) => ({ schluessel: r.id, titel: r.name })), { schluessel: "", titel: "Ohne Ressort" }]
        : (Object.keys(STATUS) as Status[]).map((st) => ({ schluessel: st, titel: STATUS[st] }));
  // Nach Person: Eine Aufgabe steht bei jeder ihrer Verantwortlichen (Stufe 2), ohne Verantwortliche unter Offen
  const personenVon = (a: T) => a.personen ?? (a.person ? [a.person] : []);
  const passt = (a: T, schluessel: string) =>
    art === "person"
      ? schluessel === "" ? personenVon(a).length === 0 : personenVon(a).includes(schluessel)
      : (art === "ressort" ? a.ressort_id ?? "" : a.status) === schluessel;
  return reihen
    .map((r) => ({ ...r, liste: sortiert.filter((a) => passt(a, r.schluessel)) }))
    .filter((g) => g.liste.length > 0);
}

export interface SprintFortschritt {
  gesamt: number;
  erledigt: number;
  /** Prozent, ganzzahlig */
  prozent: number;
  jeStatus: Record<Status, number>;
}

/** Fortschritt eines Sprints: Prozent und die Zahlen je Status für den Balken mit Legende (Status nie nur als Farbe). */
export function sprintFortschritt(liste: readonly Pick<Aufgabe, "status">[]): SprintFortschritt {
  const jeStatus: Record<Status, number> = { zu_tun: 0, in_arbeit: 0, wartet: 0, erledigt: 0 };
  for (const a of liste) jeStatus[a.status] += 1;
  const gesamt = liste.length;
  return { gesamt, erledigt: jeStatus.erledigt, prozent: gesamt ? Math.round((jeStatus.erledigt / gesamt) * 100) : 0, jeStatus };
}

/** Vorschlag für das Ende des nächsten Sprints, wie aufgaben_sprint_vorschlag() in der Datenbank:
 *  die nächste geplante Vorstandssitzung nach dem bisherigen Ende, sonst 14 Tage später. */
export function vorschlagEnde(ende: string, sitzungen: readonly { datum: string; status: string }[]): string {
  const naechste = sitzungen
    .filter((t) => (t.status === "geplant" || t.status === "verschoben") && t.datum > ende)
    .map((t) => t.datum)
    .sort()[0];
  return naechste ?? plusTage(ende, 14);
}

/** „Abschließen: 12 übernehmen, 2 in den Backlog“ */
export function abschlussText(weiter: number, backlog: number): string {
  return `Abschließen: ${weiter} übernehmen, ${backlog} in den Backlog`;
}

/** Wo liegt eine Aufgabe? „Sprint 26.10.“, „Backlog“ oder „Archiv, Sprint 12.10.“ */
export function ortText(a: Pick<Aufgabe, "sprint_name" | "sprint_status">): string {
  if (!a.sprint_name) return "Backlog";
  return a.sprint_status === "abgeschlossen" ? `Archiv, ${a.sprint_name}` : a.sprint_name;
}

export type Ort = "sprint" | "backlog" | "archiv";

export function ortVon(a: Pick<Aufgabe, "sprint_id" | "sprint_status">): Ort {
  if (!a.sprint_id) return "backlog";
  return a.sprint_status === "abgeschlossen" ? "archiv" : "sprint";
}
